import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SEO, getBreadcrumbSchema } from '@/components/seo/SEO';
import { 
  FloralCorner, 
  FloralSeparator,
  PaperLayerBg 
} from '@/components/ui/FloralElements';
import { 
  Leaf, 
  Heart, 
  TreePine, 
  Sun,
  Mountain,
  ArrowRight,
  ShoppingBag,
  ExternalLink
} from 'lucide-react';

// Ranch photos
const ranchPhotos = {
  calves: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/9exx0j39_IMG_2561.JPEG',
  horses: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/m973uo9g_IMG_2599.jpg',
  cowCalf: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/uc00byr0_IMG_3232.JPEG',
  herd: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/enbbzva9_IMG_4574.jpeg',
  goatKid: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/ybcfo7j1_IMG_6810.jpeg',
  womanGoat: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/n5mw12s5_IMG_6869.JPG',
  childGoat: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/1ejb7upe_IMG_7758.JPG',
  aurora: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/h2mpppe9_IMG_8669.jpeg',
  apron: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/xuuv78u0_0%203.21.09%E2%80%AFPM.jpg',
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

export const AboutPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' }
  ];

  return (
    <>
      <SEO
        title="About Us"
        description="Learn about Little House on the Table - a 7th generation Nebraska family ranch in Crawford, NE. Discover our story of sustainable agriculture, cattle ranching, and handmade products crafted with love."
        path="/about"
        structuredData={getBreadcrumbSchema(breadcrumbs)}
        keywords="Nebraska family ranch, about us, 7th generation ranch, Crawford Nebraska, sustainable agriculture, cattle ranch, family farm story"
      />
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-hero overflow-hidden">
        <FloralCorner position="top-left" className="opacity-40" />
        <FloralCorner position="bottom-right" className="opacity-40" />
        
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4"
            >
              <TreePine className="h-4 w-4" />
              Our Story
            </motion.span>
            <motion.h1 
              variants={fadeInUp}
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6"
            >
              About Little House on the Table
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl text-muted-foreground"
            >
              Rooted in real, working land — a 7th generation Nebraska family ranch 
              committed to sustainable agriculture and stewardship.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main Story Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-6">
                A Working Cattle Ranch with Heart
              </h2>
              
              <div className="prose prose-lg text-muted-foreground space-y-4">
                <p>
                  Little House on the Table is rooted in real, working land. We are a working cattle ranch 
                  with a cow-calf operation and a handful of broodmares, and our days revolve around the 
                  rhythms of the seasons—caring for animals, maintaining fences, moving with the weather, 
                  and doing the steady work that keeps a ranch running.
                </p>
                
                <p>
                  Our mission is simple: practice sustainable agriculture and stewardship. We believe in 
                  working with the land—protecting soil health, respecting pasture cycles, and making 
                  thoughtful choices that support long-term resilience for our animals, our fields, and 
                  our community.
                </p>
                
                <p>
                  Everything we share is an extension of that way of life: honest work, practical knowledge, 
                  and a deep appreciation for the beauty that comes from living close to the land.
                </p>
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <PaperLayerBg>
                <img
                  src={ranchPhotos.aurora}
                  alt="Beautiful aurora borealis over our Nebraska ranch barn - stunning night sky"
                  className="w-full h-[400px] object-cover rounded-xl"
                />
              </PaperLayerBg>
            </motion.div>
          </div>
        </div>
      </section>

      <FloralSeparator />

      {/* Values Section */}
      <section className="py-20 bg-secondary relative">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4"
            >
              Our Values
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-muted-foreground max-w-2xl mx-auto"
            >
              The principles that guide everything we do on the ranch
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {[
              {
                icon: Leaf,
                title: 'Sustainable Agriculture',
                description: 'We protect soil health and respect natural pasture cycles for long-term resilience.'
              },
              {
                icon: Heart,
                title: 'Ethical Stewardship',
                description: 'Our animals are treated with care and respect—they are family, not just livestock.'
              },
              {
                icon: Sun,
                title: 'Natural Products',
                description: 'No chemicals or shortcuts. Just honest, natural products from our ranch to your home.'
              },
              {
                icon: Mountain,
                title: 'Working the Land',
                description: 'We believe in the value of hard work and the wisdom that comes from living close to nature.'
              }
            ].map((value, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="raised-card h-full text-center p-6 border-0">
                  <CardContent className="p-0">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                      <value.icon className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="font-serif text-lg font-semibold text-foreground mb-2">
                      {value.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Ranch Life Gallery */}
      <section className="py-20 relative overflow-hidden">
        <FloralCorner position="top-right" className="opacity-30" />
        
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4"
            >
              Life on the Ranch
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-muted-foreground"
            >
              Glimpses of our daily life in Northwest Nebraska
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {[
              { src: ranchPhotos.womanGoat, alt: 'Ranch owner with baby goat' },
              { src: ranchPhotos.childGoat, alt: 'Child bottle feeding a goat' },
              { src: ranchPhotos.horses, alt: 'Family on horseback' },
              { src: ranchPhotos.cowCalf, alt: 'Mother cow with newborn calf' },
              { src: ranchPhotos.calves, alt: 'Young rancher with calves' },
              { src: ranchPhotos.herd, alt: 'Cattle herd with ranch dog' },
              { src: ranchPhotos.goatKid, alt: 'Child with baby goat' },
              { src: ranchPhotos.apron, alt: 'Handmade apron with canned goods' },
            ].map((photo, index) => (
              <motion.div 
                key={index} 
                variants={fadeInUp}
              >
                <div className="raised-card overflow-hidden rounded-xl">
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full h-48 md:h-56 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-floral relative">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4">
              Bring the Ranch to Your Home
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Explore our handmade products crafted with care from our Nebraska family ranch.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg"
                className="btn-beveled px-8 py-6 text-base font-semibold"
              >
                <a
                  href="https://littlehouseonthetable.square.site/s/shop"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <ShoppingBag className="h-5 w-5" />
                  Shop Our Products
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
              <Button 
                asChild 
                variant="outline"
                size="lg"
                className="border-2 border-primary/30 hover:bg-primary/5 px-8 py-6"
              >
                <Link to="/goats" className="flex items-center gap-2">
                  Meet Our Goats
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
